import { useEffect, useState } from "react";
import { AudioLines, Loader2, ShieldCheck, Timer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { Assistant, ResponsePace } from "../api";
import { useUpdateAssistant } from "../hooks";

const PACES: { value: ResponsePace; label: string; hint: string }[] = [
  { value: "snappy", label: "Snappy", hint: "Replies fast. May cut people off mid-thought." },
  { value: "balanced", label: "Balanced", hint: "The default. Works for most conversations." },
  { value: "relaxed", label: "Relaxed", hint: "Waits longer. Better for slower speakers." },
];

const MAX_CALL_MINUTES = 180;
const MAX_SILENCE_SECONDS = 300;

/** The settings the Advanced tab owns, mirrored from the assistant. */
type Draft = Pick<
  Assistant,
  | "response_pace"
  | "speculative_replies"
  | "wait_after_question"
  | "record_calls"
  | "max_call_duration_minutes"
  | "silence_timeout_seconds"
>;

function draftOf(a: Assistant): Draft {
  return {
    response_pace: a.response_pace,
    speculative_replies: a.speculative_replies ?? false,
    wait_after_question: a.wait_after_question ?? true,
    record_calls: a.record_calls,
    max_call_duration_minutes: a.max_call_duration_minutes,
    silence_timeout_seconds: a.silence_timeout_seconds,
  };
}

/**
 * Advanced tab: how a call behaves, as opposed to what the assistant says.
 *
 * Deliberately short. Every control here is honoured by the voice pipelines on
 * both carriers — a switch that a call can't actually obey is worse than no
 * switch at all, so nothing goes in until the pipeline side exists.
 */
export default function AssistantAdvanced({ assistant }: { assistant: Assistant }) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(assistant));
  const [dirty, setDirty] = useState(false);
  const updateMut = useUpdateAssistant(assistant.id);

  // Adopt server changes (e.g. after a version restore) unless mid-edit.
  useEffect(() => {
    if (!dirty) setDraft(draftOf(assistant));
  }, [assistant, dirty]);

  function patch(part: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...part }));
    setDirty(true);
  }

  const silence = draft.silence_timeout_seconds;
  const silenceError =
    silence !== 0 && silence < 5
      ? "Use at least 5 seconds, or 0 to never hang up on silence."
      : silence > MAX_SILENCE_SECONDS
      ? `At most ${MAX_SILENCE_SECONDS} seconds.`
      : "";
  const durationError =
    draft.max_call_duration_minutes > MAX_CALL_MINUTES
      ? `At most ${MAX_CALL_MINUTES} minutes.`
      : "";
  // Speculative replies need Flux's turn predictions.
  const flux = /flux/i.test(assistant.transcriber?.name ?? "");
  const canSave = dirty && !silenceError && !durationError && !updateMut.isPending;

  function save() {
    updateMut.mutate(draft, {
      onSuccess: () => {
        setDirty(false);
        toast.success("Advanced settings saved");
      },
    });
  }

  return (
    <div className="space-y-6">
      {/* ── Privacy ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="size-4 text-emerald-500" />
            Privacy
          </CardTitle>
          <CardDescription>
            What is kept after the caller hangs up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium">Record calls</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Keeps the call audio on the call log so you can listen back.
                Turn off where consent rules don't allow recording — the written
                transcript is kept either way.
              </p>
            </div>
            <Switch
              checked={draft.record_calls}
              onCheckedChange={(v) => patch({ record_calls: v })}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Call limits ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Timer className="size-4 text-amber-500" />
            Call limits
          </CardTitle>
          <CardDescription>
            A call that never ends still bills by the minute. These two end it
            for you.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <NumberField
            label="Maximum call length"
            unit="minutes"
            value={draft.max_call_duration_minutes}
            onChange={(v) => patch({ max_call_duration_minutes: v })}
            max={MAX_CALL_MINUTES}
            error={durationError}
            hint={
              draft.max_call_duration_minutes
                ? `Hangs up once the call reaches ${draft.max_call_duration_minutes} minutes.`
                : "0 — a call can run as long as it needs to."
            }
          />
          <NumberField
            label="Hang up after silence"
            unit="seconds"
            value={draft.silence_timeout_seconds}
            onChange={(v) => patch({ silence_timeout_seconds: v })}
            max={MAX_SILENCE_SECONDS}
            error={silenceError}
            hint={
              silence
                ? `Ends the call after ${silence}s with nothing from the caller.`
                : "0 — a caller who walks away stays connected."
            }
          />
        </CardContent>
      </Card>

      {/* ── Turn taking ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AudioLines className="size-4 text-sky-500" />
            Turn taking
          </CardTitle>
          <CardDescription>
            How long the assistant waits after the caller stops talking before
            it replies.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-3">
          {PACES.map((p) => {
            const active = draft.response_pace === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => patch({ response_pace: p.value })}
                className={cn(
                  "rounded-lg border p-3 text-left transition-colors",
                  active
                    ? "border-primary bg-primary/5"
                    : "hover:border-muted-foreground/30 hover:bg-muted/40",
                )}
              >
                <div className="text-sm font-medium">{p.label}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{p.hint}</div>
              </button>
            );
          })}
          <div className="flex items-start justify-between gap-4 pt-3 sm:col-span-3">
            <div className="min-w-0">
              <div className="text-sm font-medium">Wait for an answer after asking a question</div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                When the assistant asks something, it stops there and listens:
                nothing it wrote after the question is spoken, and tools it
                called in the same reply (ending the call, booking, an API
                request) wait until the caller has answered. Works on Telnyx
                and Twilio numbers.
              </p>
            </div>
            <Switch
              checked={draft.wait_after_question}
              onCheckedChange={(v) => patch({ wait_after_question: v })}
            />
          </div>
          {flux && (
            <div className="flex items-start justify-between gap-4 pt-3 sm:col-span-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">Speculative replies</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Starts writing the reply while Flux predicts the caller is
                  finishing, so it can be spoken the moment they stop. A wrong
                  prediction costs an extra AI call. Works on Telnyx numbers.
                </p>
              </div>
              <Switch
                checked={draft.speculative_replies}
                onCheckedChange={(v) => patch({ speculative_replies: v })}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={save} disabled={!canSave}>
          {updateMut.isPending && <Loader2 className="mr-2 size-3.5 animate-spin" />}
          Save changes
        </Button>
        {dirty && (
          <span className="text-xs text-amber-600 dark:text-amber-500">
            Unsaved changes
          </span>
        )}
      </div>
    </div>
  );
}

/** A whole-number input that treats an emptied box as 0 rather than NaN. */
function NumberField({
  label,
  unit,
  value,
  onChange,
  max,
  hint,
  error,
}: {
  label: string;
  unit: string;
  value: number;
  onChange: (v: number) => void;
  max: number;
  hint: string;
  error: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={max}
          value={String(value)}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
          }}
          className="h-9 w-28"
        />
        <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
      <p className={cn("text-[11px]", error ? "text-destructive" : "text-muted-foreground")}>
        {error || hint}
      </p>
    </div>
  );
}
